import {
  BookerCartPriceQuery,
  EnumGuestTierType,
  EnumPriceTierPriceType,
  EnumProductType,
  Exact,
  InputMaybe,
  OrderDeliveryInput,
  Product,
  ProductPurchaseRequest,
  PurchasedProductGuestsPerTier,
  Scalars,
} from "gql/generated";
import { LazyQueryExecFunction } from "@apollo/client";
import { CartItem, DiscountItem } from "./cartContextTypes";
import { getMultilingualString } from "shared/utils";
import { getTotalGuestCount } from "widgets/booker/shared/utils";

const MIN_TAX_EXEMPTION_NUMBER_LENGTH = 5;
const MAX_TAX_EXEMPTION_NUMBER_LENGTH = 30;

/**
 * Mirrors the backend's `sanitizeTaxExemptionNumber` length rule. A claimed
 * exemption is only priced (tax zeroed) once the number passes this check —
 * the number is required, not optional.
 */
export function isValidTaxExemptionNumber(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length >= MIN_TAX_EXEMPTION_NUMBER_LENGTH &&
    trimmed.length <= MAX_TAX_EXEMPTION_NUMBER_LENGTH
  );
}

export function getAppropriateGuestTiers(
  guestTiers: PurchasedProductGuestsPerTier[],
  productPricing: Product["pricing"],
  isProductPerGuestPriceType: boolean,
  includeFullTierData: boolean = false,
) {
  // if the main product is using per night pricing, they return the
  // original guest tiers
  if (!isProductPerGuestPriceType) return guestTiers;

  // For per guest pricing, we need to get the correct tier id & return that:

  // Get the full price tiers with the guest counts for each tier
  const fullTiersWithGuestCount = guestTiers.map(tier => {
    const fullTier = productPricing?.tiers.find(t => t?._id === tier.tierId);
    return {
      ...fullTier,
      guestCount: tier.count,
    };
  });

  // Get the total guest count for the cart item
  const totalGuestCount = getTotalGuestCount(guestTiers);

  // Get the appropriate tier for the total guest count.
  return fullTiersWithGuestCount.map(tier => {
    // Find the appropriate tier based on the total guest count,
    // type and label
    const appropriateTierPerSelectedGuestCount = productPricing?.tiers.find(
      t =>
        t?.guestCountForPrice === totalGuestCount &&
        t.type === tier.type &&
        getMultilingualString(t.label ?? {}) ===
          getMultilingualString(tier.label ?? {}),
    );

    // Return the appropriate tier ID and guest count
    return {
      tierId: appropriateTierPerSelectedGuestCount?._id ?? tier._id,
      count: tier.guestCount,
      ...(includeFullTierData
        ? { fullTier: appropriateTierPerSelectedGuestCount ?? tier }
        : {}),
    };
  });
}

/** Splits a free-text full name into first/last for prefilling the customer form. */
export function splitGuestName(fullName: string): {
  firstName: string;
  lastName: string;
} {
  const trimmed = fullName.trim().replace(/\s+/g, " ");
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(" ");
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

/** Default guest tier rows when adding the main product to the cart (matches CartContext init). */
export function buildInitialGuestsPerTierForProduct(
  product: Product | null | undefined,
): PurchasedProductGuestsPerTier[] {
  if (!product?.pricing?.tiers?.length) return [];

  const isMainProductFee = product.type === EnumProductType.Fee;
  const isRenewableFee = product.type === EnumProductType.RenewableFee;
  const isMerch = product.type === EnumProductType.Merch;
  const isOneTimeSellable = isMainProductFee || isMerch || isRenewableFee;

  const doesMainProductHavePerGuestPricing = isOneTimeSellable
    ? false
    : !!product.pricing.tiers.some(
        tier => tier?.priceType === EnumPriceTierPriceType.PerGuest,
      );

  if (doesMainProductHavePerGuestPricing) {
    return (product.pricing.tiers ?? [])
      .filter(tier => !!tier)
      .reduce(
        (acc, tier) => {
          const type = tier.type;
          const label = getMultilingualString(tier.label ?? {});
          const doesTierExist = acc.some(
            t => t.type === type && t.label === label,
          );
          if (!doesTierExist) {
            acc.push({
              _id: tier._id,
              type: tier.type,
              label: getMultilingualString(tier.label ?? {}),
            });
          }
          return acc;
        },
        [] as { _id: string; type: EnumGuestTierType; label: string }[],
      )
      .map(tier => ({
        count: 0,
        tierId: tier._id,
      })) as PurchasedProductGuestsPerTier[];
  }

  return (product.pricing.tiers ?? []).map(tier => ({
    count: 0,
    tierId: tier?._id,
  })) as PurchasedProductGuestsPerTier[];
}

function convertCartToProductPurchaseRequests(
  cart: CartItem[],
  mainProductPricing: Product["pricing"],
  selectedTierId?: string | null,
): ProductPurchaseRequest[] {
  const isMainProductPerGuestPricing = !!mainProductPricing?.tiers.some(
    tier => tier?.priceType === EnumPriceTierPriceType.PerGuest,
  );

  return cart.map((item, idx) => {
    const guestTiers = getAppropriateGuestTiers(
      item.guestsPerTier,
      idx === 0 ? mainProductPricing : undefined,
      idx === 0 ? isMainProductPerGuestPricing : false,
      false,
    );
    // Cart keys for merch (productId::variantId) and event sessions
    // (productId::sessionId, where sessionId may itself contain "::") are
    // composite. Product ids are ObjectIds and never contain "::", so the first
    // segment is always the real product id.
    const realProductId = item.productId.split("::")[0];

    return {
      startDateISO: item.startDateISO ?? "",
      endDateISO: item.endDateISO ?? "",
      productId: realProductId,
      guestTiers,
      quantity: item.quantity,
      ...(item.sessionId ? { sessionId: item.sessionId } : {}),
      ...(item.sessionIds?.length ? { sessionIds: item.sessionIds } : {}),
      ...(idx === 0 && selectedTierId ? { tierId: selectedTierId } : {}),
      ...(item.merchVariantId ? { merchVariantId: item.merchVariantId } : {}),
      ...(item.customFieldResponses?.length
        ? { customFieldResponses: item.customFieldResponses }
        : {}),
      ...(item.businessScopes?.length
        ? { businessScopes: toBusinessScopesInput(item.businessScopes) }
        : {}),
    };
  });
}

function toBusinessScopesInput(scopes: NonNullable<CartItem["businessScopes"]>) {
  return scopes.map(scope => ({
    operatorId: scope.operatorId,
    ...(scope.customFieldResponses?.length
      ? { customFieldResponses: scope.customFieldResponses }
      : {}),
  }));
}

type AdjustCartPricingProps = {
  getCartPrices: LazyQueryExecFunction<
    BookerCartPriceQuery,
    Exact<{
      bookerId: Scalars["ID"]["input"];
      productPurchaseRequests:
        | Array<ProductPurchaseRequest>
        | ProductPurchaseRequest;
      promoCode?: InputMaybe<Scalars["String"]["input"]>;
      taxExempt?: InputMaybe<Scalars["Boolean"]["input"]>;
      delivery?: InputMaybe<OrderDeliveryInput>;
    }>
  >;
  bookerID: string;
  setTotals: React.Dispatch<
    React.SetStateAction<{
      subTotal: number;
      tax: number;
      taxLines: { name: string; ratePercent?: number | null; amount: number }[];
      grandTotal: number;
      depositTotal: number;
      customerFee: number;
    }>
  >;
  setCart: React.Dispatch<React.SetStateAction<CartItem[]>>;
  newCart: CartItem[];
  setDiscounts: React.Dispatch<React.SetStateAction<DiscountItem[]>>;
  promoCode: string;
  setPromoCode: React.Dispatch<React.SetStateAction<string>>;
  setPromoCodeError: React.Dispatch<React.SetStateAction<boolean>>;
  mainProductPricing: Product["pricing"];
  selectedTierId?: string | null;
  /** When set, skip applying async pricing if a newer request was started (avoids stale overwrites). */
  isStale?: () => boolean;
  /** Self-declared tax exemption toggle; only honored server-side when the booker offers it. */
  taxExempt?: boolean;
  delivery?: OrderDeliveryInput | null;
  setShipping?: (shipping: {
    total: number;
    available: boolean | null;
  }) => void;
};

function isAccommodationCartItemPricable(item: CartItem): boolean {
  if (item.productType !== EnumProductType.Accommodation) return true;
  return (
    (item.startDateISO?.length ?? 0) > 0 &&
    (item.endDateISO?.length ?? 0) > 0
  );
}

function adjustCartPricing({
  getCartPrices,
  bookerID,
  setTotals,
  setCart,
  newCart,
  setDiscounts,
  promoCode,
  setPromoCode,
  setPromoCodeError,
  mainProductPricing,
  selectedTierId,
  isStale,
  taxExempt,
  delivery,
  setShipping,
}: AdjustCartPricingProps) {
  const pricableItems = newCart.filter(isAccommodationCartItemPricable);

  setCart(newCart);

  if (pricableItems.length === 0) {
    setTotals({ subTotal: 0, grandTotal: 0, tax: 0, taxLines: [], depositTotal: 0, customerFee: 0 });
    setDiscounts([]);
    setShipping?.({ total: 0, available: null });
    return;
  }

  if (pricableItems.length > 0) {
    getCartPrices({
      variables: {
        bookerId: bookerID,
        productPurchaseRequests: convertCartToProductPurchaseRequests(
          pricableItems,
          mainProductPricing,
          selectedTierId,
        ),
        promoCode: promoCode.length > 0 ? promoCode : undefined,
        taxExempt: !!taxExempt,
        delivery: delivery ?? undefined,
      },
    })
      .then(res => {
        if (isStale?.()) return;
        const newTotals = res.data?.bookerCartPrice;
        if (newTotals) {
          setTotals({
            subTotal: newTotals.subTotal,
            grandTotal: newTotals.grandTotal,
            tax: newTotals.taxTotal,
            taxLines: (newTotals.taxLines ?? []).map(line => ({
              name: line.name,
              ratePercent: line.ratePercent,
              amount: line.amount,
            })),
            depositTotal: newTotals.depositTotal,
            customerFee: newTotals.customerFee,
          });

          setDiscounts(newTotals.discountTotals);
          setShipping?.({
            total: newTotals.shippingTotal ?? 0,
            available: newTotals.deliveryAvailable ?? null,
          });

          // A tier-scoped promo code can become inapplicable after the fact
          // (e.g. the customer changes tiers post-apply). The backend already
          // priced this response as if the code weren't applied - we just
          // need to clear it client-side and surface an error.
          if (promoCode.length > 0 && newTotals.promoCodeApplicable === false) {
            setPromoCode("");
            setPromoCodeError(true);
          } else {
            setPromoCodeError(false);
          }

          let priceIdx = 0;
          const cartWithPrice = newCart.map(item => {
            const isPricable = isAccommodationCartItemPricable(item);
            if (isPricable && priceIdx < newTotals.productPrices.length) {
              const serverPrice = newTotals.productPrices[priceIdx];
              priceIdx++;
              return { ...item, total: serverPrice.price };
            }
            return item;
          });

          // Answers typed while pricing was in flight must survive.
          setCart(current =>
            cartWithPrice.map(item => {
              const live = current.find(c => c.productId === item.productId);
              if (!live) return item;
              return {
                ...item,
                ...(live.customFieldResponses !== item.customFieldResponses
                  ? { customFieldResponses: live.customFieldResponses }
                  : {}),
                ...(live.businessScopes !== item.businessScopes
                  ? { businessScopes: live.businessScopes }
                  : {}),
              };
            }),
          );
        }
      })
      .catch(err => {
        throw new Error(err);
      });
  }
}

export {
  convertCartToProductPurchaseRequests,
  adjustCartPricing,
};
